import React from "react";
import { Clock, AlertTriangle, Ruler, Repeat } from "lucide-react";

const pains = [
  {
    icon: Clock,
    title: "Time Consuming",
    desc: "Every gland position re-measured, re-marked, re-checked. Minutes lost on every panel.",
  },
  {
    icon: Repeat,
    title: "Inconsistent Spacing",
    desc: "Hand-marked spacing drifts across the row — and across the team.",
  },
  {
    icon: AlertTriangle,
    title: "Human Error",
    desc: "A slipped pencil, a misread tape, a rushed job. Mistakes cost time and reputation.",
  },
  {
    icon: Ruler,
    title: "Costly Rework",
    desc: "Drill the wrong hole and the enclosure is scrap. Rework eats margin on every contract.",
  },
];

export const Problem = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 md:py-36 border-t border-[#27272A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">01 / The Problem</span>
            <h2
              data-testid="problem-headline"
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tighter mt-4"
            >
              Tape measure.
              <br />
              Pencil. Square.
              <br />
              <span className="text-[#A1A1AA]">Repeat. Repeat. Repeat.</span>
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base md:text-lg max-w-md leading-relaxed">
              Traditional gland marking hasn&apos;t changed in decades. It relies on
              improvised measurements, manual marking and time-pressured guesswork.
              The result? Panels that look untidy, jobs that overrun and reputations
              that take a hit.
            </p>

            <div className="mt-8 relative aspect-[4/3] border border-[#27272A] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1635335874521-7987db781153"
                alt="Messy traditional wiring in switch enclosure"
                className="w-full h-full object-cover opacity-90"
              />
            </div>
            <div className="mt-3 font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA]">
              The Old Way · Manual Marking
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-[#27272A] border border-[#27272A]">
              {pains.map((p) => (
                <div
                  key={p.title}
                  data-testid={`pain-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#0A0A0A] p-7 md:p-9 hover:bg-[#141414] transition-colors group"
                >
                  <div className="w-12 h-12 grid place-items-center bg-[#1F1F1F] border border-[#27272A] rounded-sm mb-5 group-hover:border-[#FF6B00] transition-colors">
                    <p.icon className="w-5 h-5 text-[#FF6B00]" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl mb-2">
                    {p.title}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm md:text-[15px] leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 md:p-8 border border-[#27272A] bg-[#141414] rounded-sm">
              <div className="flex items-start gap-4">
                <div className="font-display text-4xl text-[#FF6B00] leading-none">
                  £
                </div>
                <div>
                  <div className="font-display text-2xl md:text-3xl">
                    Wasted hours. Wasted enclosures.
                  </div>
                  <p className="text-[#A1A1AA] mt-2 max-w-2xl leading-relaxed">
                    Every contract gets squeezed by the same hidden costs: extra
                    time on the bench, scrapped panels, and call-backs. The trade
                    needs a faster, repeatable, professional way.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
